import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import { FaChevronDown, FaChevronRight, FaFileExcel, FaSpinner, FaTimes } from 'react-icons/fa';
import { notify } from '../../../components/Utils/Notifications';

const EMPTY_FILTERS = { item: '', employee: '', matricule: '' };

// Minuscules sans accents : « Écart » doit trouver « ecart ».
const normalizeSearch = (value) => String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// Même logique que UserPayslipController._filter_variation_data côté Odoo, pour
// que l'export Excel reflète l'écran. Avec un filtre employé ou matricule, les
// montants de la rubrique sont recalculés sur les seuls employés retenus.
export const filterVariations = (sections, filters) => {
    const item = normalizeSearch(filters.item);
    const employee = normalizeSearch(filters.employee);
    const matricule = normalizeSearch(filters.matricule);
    if (!item && !employee && !matricule) return sections;

    return sections
        .map((section) => ({
            ...section,
            lines: section.lines.flatMap((line) => {
                if (item && !normalizeSearch(line.name).includes(item) && !normalizeSearch(line.code).includes(item)) {
                    return [];
                }
                if (!employee && !matricule) return [line];

                const employees = line.employees.filter((emp) =>
                    (!employee || normalizeSearch(emp.name).includes(employee))
                    && (!matricule || normalizeSearch(emp.matricule).includes(matricule)));
                if (employees.length === 0) return [];

                const m1 = employees.reduce((sum, emp) => sum + emp.m1, 0);
                const m = employees.reduce((sum, emp) => sum + emp.m, 0);
                const diff = m - m1;
                const pct = m1 !== 0 ? Math.round((diff / m1) * 10000) / 100 : null;
                return [{ ...line, employees, m1, m, diff, pct }];
            }),
        }))
        .filter((section) => section.lines.length > 0);
};

const PayslipVariationReport = ({ month }) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [constantPerimeter, setConstantPerimeter] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const filteredData = useMemo(() => filterVariations(data, filters), [data, filters]);
    const hasFilters = Object.values(filters).some((value) => value.trim() !== '');
    // Filtrer sur un employé sans voir ses lignes n'a pas de sens : on déplie.
    const hasEmployeeFilter = filters.employee.trim() !== '' || filters.matricule.trim() !== '';
    const isExpanded = (code) => hasEmployeeFilter || expandedRows.has(code);

    const updateFilter = (key) => (event) => setFilters((previous) => ({ ...previous, [key]: event.target.value }));

    useEffect(() => {
        if (month) {
            fetchData();
        }
    }, [month, constantPerimeter]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.getElement(`/payslip/variation?month=${month}&constant_perimeter=${constantPerimeter}`);
            if (response && response.data && response.data.variations) {
                setData(response.data.variations);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error(error);
            notify(t('payslipVariationReport.loadError'), "top-right", 3);
        } finally {
            setIsLoading(false);
        }
    };

    const handleExport = async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ month, constant_perimeter: String(constantPerimeter) });
            Object.entries(filters).forEach(([key, value]) => {
                if (value.trim()) params.append(key, value.trim());
            });
            const response = await apiService.getBlobFile(`/payslip/variation/export?${params.toString()}`);
            if (response && response.data) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Variation_Paie_${month}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
            }
        } catch (error) {
            console.error(error);
            notify(t('payslipVariationReport.exportError'), "top-right", 3);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleRow = (code) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(code)) {
            newExpandedRows.delete(code);
        } else {
            newExpandedRows.add(code);
        }
        setExpandedRows(newExpandedRows);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat(dateLocale, { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(amount || 0).replace('XOF', 'FCFA');
    };

    const getVariationBadge = (pct) => {
        if (pct === null) return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full whitespace-nowrap">{t('payslipVariationReport.new')}</span>;
        if (pct > 0) return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full whitespace-nowrap">+{pct.toFixed(2)}%</span>;
        if (pct < 0) return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full whitespace-nowrap">{pct.toFixed(2)}%</span>;
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full whitespace-nowrap">0%</span>;
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex-1 w-full mx-auto max-w-[calc(100vw-300px)]">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {constantPerimeter ? t('payslipVariationReport.constantPerimeterAnalysis') : t('payslipVariationReport.globalAnalysis')}
                    </h2>
                    <label className="flex items-center gap-2 cursor-pointer bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-[--color-primary] rounded cursor-pointer"
                            checked={constantPerimeter}
                            onChange={(e) => setConstantPerimeter(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700 select-none">{t('payslipVariationReport.excludeEntriesExits')}</span>
                    </label>
                </div>

                <button
                    onClick={handleExport}
                    disabled={isLoading || filteredData.length === 0}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <FaFileExcel /> {isLoading ? t('payslipVariationReport.loading') : t('payslipVariationReport.exportExcel')}
                </button>
            </div>

            {data.length > 0 && (
                <div className="flex flex-wrap items-end gap-3 mb-4">
                    {[
                        ['item', t('payslipVariationReport.filterItem')],
                        ['employee', t('payslipVariationReport.filterEmployee')],
                        ['matricule', t('payslipVariationReport.filterMatricule')],
                    ].map(([key, label]) => (
                        <label key={key} className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                            {label}
                            <input
                                type="search"
                                name={`variation-filter-${key}`}
                                value={filters[key]}
                                onChange={updateFilter(key)}
                                placeholder={t('payslipVariationReport.filterPlaceholder')}
                                className="w-56 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                            />
                        </label>
                    ))}
                    {hasFilters && (
                        <button
                            type="button"
                            onClick={() => setFilters(EMPTY_FILTERS)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
                        >
                            <FaTimes /> {t('payslipVariationReport.resetFilters')}
                        </button>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center items-center py-20">
                    <FaSpinner className="animate-spin text-gray-400 text-3xl" />
                </div>
            ) : data.length === 0 ? (
                <div className="text-center py-20 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-lg">{t('payslipVariationReport.noDataFound')}</p>
                    <p className="text-sm mt-2">{t('payslipVariationReport.noDataHelp')}</p>
                </div>
            ) : filteredData.length === 0 ? (
                <div className="text-center py-20 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-lg">{t('payslipVariationReport.noFilterMatch')}</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-[#f8fafc]">
                            <tr>
                                <th scope="col" className="px-6 py-4 text-left font-semibold text-gray-600 uppercase tracking-wider">{t('payslipVariationReport.itemEmployee')}</th>
                                <th scope="col" className="px-6 py-4 text-left font-semibold text-gray-600 uppercase tracking-wider">{t('payslipVariationReport.matricule')}</th>
                                <th scope="col" className="px-6 py-4 text-right font-semibold text-gray-600 uppercase tracking-wider">{t('payslipVariationReport.amountM1')}</th>
                                <th scope="col" className="px-6 py-4 text-right font-semibold text-gray-600 uppercase tracking-wider">{t('payslipVariationReport.amountM')}</th>
                                <th scope="col" className="px-6 py-4 text-right font-semibold text-gray-600 uppercase tracking-wider">{t('payslipVariationReport.difference')}</th>
                                <th scope="col" className="px-6 py-4 text-right font-semibold text-gray-600 uppercase tracking-wider">{t('payslipVariationReport.variation')}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredData.map((section) => (
                                <React.Fragment key={section.id}>
                                    <tr className="bg-slate-100/80 border-t-2 border-slate-200">
                                        <td colSpan="6" className="px-6 py-2 text-sm font-bold text-slate-700 uppercase tracking-wide">
                                            {section.title}
                                        </td>
                                    </tr>
                                    {section.lines.map((row) => (
                                        <React.Fragment key={row.code}>
                                            <tr 
                                                className={`hover:bg-blue-50/50 cursor-pointer transition-colors ${isExpanded(row.code) ? 'bg-blue-50/30' : ''} ${row.is_total ? 'bg-indigo-50/30' : ''}`}
                                                onClick={() => toggleRow(row.code)}
                                            >
                                                <td className={`px-6 py-4 whitespace-nowrap flex items-center gap-3 ${row.is_total ? 'font-bold text-indigo-900 border-l-4 border-indigo-500 pl-5' : 'font-semibold text-gray-800'}`}>
                                                    {!row.is_total && (isExpanded(row.code) ? <FaChevronDown className="text-gray-400 text-xs"/> : <FaChevronRight className="text-gray-400 text-xs"/>)}
                                                    {row.is_total && <span className="w-3"></span>}
                                                    {row.name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap"></td>
                                                <td className={`px-6 py-4 whitespace-nowrap text-right ${row.is_total ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{formatCurrency(row.m1)}</td>
                                                <td className={`px-6 py-4 whitespace-nowrap text-right ${row.is_total ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>{formatCurrency(row.m)}</td>
                                                <td className={`px-6 py-4 whitespace-nowrap text-right font-bold ${row.is_total ? 'text-indigo-900' : (row.diff > 0 ? 'text-gray-900' : row.diff < 0 ? 'text-gray-900' : 'text-gray-500')}`}>
                                                    {row.diff > 0 ? '+' : ''}{formatCurrency(row.diff)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                                    {getVariationBadge(row.pct)}
                                                </td>
                                            </tr>
                                            
                                            {isExpanded(row.code) && row.employees.map((emp) => (
                                                <tr key={emp.id} className="bg-gray-50/70 hover:bg-gray-100/70 transition-colors">
                                                    <td className="px-6 py-3 whitespace-nowrap text-gray-600 pl-14 text-sm font-medium border-l-4 border-transparent">
                                                        {emp.name}
                                                    </td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-gray-500 text-sm">{emp.matricule || '-'}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-right text-gray-500 text-sm">{formatCurrency(emp.m1)}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-right font-medium text-gray-700 text-sm">{formatCurrency(emp.m)}</td>
                                                    <td className={`px-6 py-3 whitespace-nowrap text-right text-sm font-medium ${emp.diff > 0 ? 'text-gray-700' : emp.diff < 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                                                        {emp.diff > 0 ? '+' : ''}{formatCurrency(emp.diff)}
                                                    </td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-right">
                                                        {getVariationBadge(emp.pct)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PayslipVariationReport;
